<template>
  <div v-if="visible" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <div class="warning-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
        </div>
        <h3 class="modal-title">Delete Department</h3>
        <p class="modal-subtitle">Are you sure you want to delete this department?</p>
      </div>

      <div class="modal-body">
        <div v-if="department" class="department-info">
          <div class="department-details">
            <div class="department-icon">🏢</div>
            <div class="department-text">
              <h4 class="department-name">{{ department.name }}</h4>
              <p class="department-id">Department ID: {{ department.id }}</p>
            </div>
          </div>
        </div>

        <div class="warning-text">
          <p><strong>This action cannot be undone.</strong></p>
          <p>All data associated with this department will be permanently removed from the system. Any agents in this department may need to be reassigned.</p>
        </div>

        <div class="form-actions">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="confirmDelete"
            class="btn btn-danger"
            :disabled="loading"
          >
            <svg v-if="loading" class="btn-spinner" width="16" height="16" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            {{ loading ? 'Deleting...' : 'Yes, Delete Department' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeleteDepartmentModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    department: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      loading: false
    }
  },
  methods: {
    async confirmDelete () {
      if (!this.department) return

      this.loading = true
      try {
        this.$emit('confirm', this.department.id)
      } catch (error) {
        console.error('Error confirming delete:', error)
      } finally {
        this.loading = false
      }
    },
    closeModal () {
      this.loading = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header {
  text-align: center;
  padding: 32px 24px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.warning-icon {
  color: #ef4444;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px;
}

.modal-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.department-info {
  margin-bottom: 24px;
}

.department-details {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.department-icon {
  font-size: 32px;
  opacity: 0.8;
}

.department-text {
  flex: 1;
}

.department-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px;
}

.department-id {
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  background: #e5e7eb;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  margin: 0;
}

.warning-text {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.warning-text p {
  margin: 0;
  color: #991b1b;
  font-size: 14px;
}

.warning-text p:first-child {
  margin-bottom: 8px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  justify-content: center;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header,
  .modal-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>
